import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from 'contexts/auth';
import { useSnackbar } from 'contexts/snackbar';
import useCompletedChatMessage from 'hooks/chat/useCompletedChatMessage';
import { Button } from 'shell/components';

const ChatCoordinatorContext = createContext(null);

const ChatCoordinator = ({ children }) => {
    const history = useHistory();
    const location = useLocation();
    const { auth } = useAuth();
    const { close, message } = useSnackbar();
    const notifiedMessageIds = useRef(new Set());
    const [openAssignmentChatId, setOpenAssignmentChatId] = useState(null);
    const [pendingOpenChatAssignmentId, setPendingOpenChatAssignmentId] = useState(null);
    const [submissionPathByAssignmentId, setSubmissionPathByAssignmentId] = useState({});

    const setSubmissionPath = useCallback((assignmentId, path) => {
        if (!assignmentId || !path) return;

        setSubmissionPathByAssignmentId((current) => ({
            ...current,
            [assignmentId]: path,
        }));
    }, []);

    const clearPendingOpenChatAssignmentId = useCallback(() => {
        setPendingOpenChatAssignmentId(null);
    }, []);

    const value = useMemo(() => {
        return {
            openAssignmentChatId,
            setOpenAssignmentChatId,
            pendingOpenChatAssignmentId,
            setPendingOpenChatAssignmentId,
            clearPendingOpenChatAssignmentId,
            submissionPathByAssignmentId,
            setSubmissionPath,
        };
    }, [
        clearPendingOpenChatAssignmentId,
        openAssignmentChatId,
        pendingOpenChatAssignmentId,
        setSubmissionPath,
        submissionPathByAssignmentId,
    ]);

    const onCompletedChatMessage = useCallback(
        (payload) => {
            const completedMessageId = payload.id || payload.message_id;
            if (!completedMessageId || notifiedMessageIds.current.has(completedMessageId)) return;

            notifiedMessageIds.current.add(completedMessageId);

            const assignmentId = payload.assignment_id;
            const isChatAlreadyOpen =
                auth?.role === 'student' &&
                Boolean(assignmentId) &&
                location.pathname.includes('/submissions/') &&
                openAssignmentChatId === assignmentId;
            if (isChatAlreadyOpen) return;

            const submissionPath = auth?.role === 'student' ? submissionPathByAssignmentId[assignmentId] : null;
            const nextPath =
                submissionPath || (assignmentId ? `/assignments/${assignmentId}/submissions` : location.pathname);

            message.success({
                text: 'New response is ready',
                action: (
                    <Button
                        sx={{ mr: 1 }}
                        color={'neutral'}
                        size={'large'}
                        variant={'borderless'}
                        onClick={() => {
                            setPendingOpenChatAssignmentId(assignmentId || null);
                            close();
                            history.push(nextPath, { openChat: true });
                        }}
                    >
                        Open
                    </Button>
                ),
            });
        },
        [
            auth?.role,
            close,
            history,
            location.pathname,
            message,
            openAssignmentChatId,
            setPendingOpenChatAssignmentId,
            submissionPathByAssignmentId,
        ]
    );

    useCompletedChatMessage(onCompletedChatMessage);

    return <ChatCoordinatorContext.Provider value={value}>{children}</ChatCoordinatorContext.Provider>;
};

const useChatCoordinator = () => useContext(ChatCoordinatorContext);

export { useChatCoordinator };
export default ChatCoordinator;
